import React, { useState, useEffect, useRef } from 'react';

const BreathingDragonGame = ({ gameData, onComplete }) => {
    const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
    const [isActive, setIsActive] = useState(false);
    const [breathingPhase, setBreathingPhase] = useState('ready'); // ready, inhale, hold, exhale, hold_after
    const [phaseTimer, setPhaseTimer] = useState(0);
    const [completedExercises, setCompletedExercises] = useState([]);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null); 
    const intervalRef = useRef(null);

    const currentExercise = gameData?.exercises?.[currentExerciseIndex];
    const pattern = currentExercise?.breathing_pattern;

    useEffect(() => {
        if (isActive && pattern) {
            const phases = [];
            if (pattern.inhale > 0) phases.push({ name: 'inhale', duration: pattern.inhale });
            if (pattern.hold > 0) phases.push({ name: 'hold', duration: pattern.hold });
            if (pattern.exhale > 0) phases.push({ name: 'exhale', duration: pattern.exhale });
            if (pattern.hold_after > 0) phases.push({ name: 'hold_after', duration: pattern.hold_after });

            let currentPhaseIndex = 0;
            let timeInPhase = 0;

            const handleVibration = (phase) => {
                if (!navigator.vibrate) return;
                switch (phase) {
                    case 'inhale': navigator.vibrate([10, 50, 10]); break;
                    case 'hold': navigator.vibrate(30); break;
                    case 'exhale': navigator.vibrate([20, 100, 20]); break;
                    case 'hold_after': navigator.vibrate(20); break;
                    default: break;
                }
            };

            intervalRef.current = setInterval(() => {
                timeInPhase++;
                setPhaseTimer(timeInPhase);

                if (timeInPhase >= phases[currentPhaseIndex].duration) {
                    currentPhaseIndex++;
                    timeInPhase = 0;
                    if (currentPhaseIndex >= phases.length) currentPhaseIndex = 0; 
                    const nextPhase = phases[currentPhaseIndex].name;
                    setBreathingPhase(nextPhase);
                    setPhaseTimer(0);
                    handleVibration(nextPhase);
                }
            }, 1000);

            setBreathingPhase(phases[0].name);
            setPhaseTimer(0);
            handleVibration(phases[0].name);

            return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
        }
    }, [isActive, pattern]);

    const startExercise = () => {
        setIsActive(true);
        setBreathingPhase('inhale');
        setPhaseTimer(0);
        if (navigator.vibrate) navigator.vibrate(50);
    };

    const stopExercise = () => {
        setIsActive(false);
        setBreathingPhase('ready');
        setPhaseTimer(0);
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (navigator.vibrate) navigator.vibrate(30);
    };

    const completeExercise = () => {
        stopExercise();
        setFeedback('correct');
        const pointsToAdd = 50;
        if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);

        if (!completedExercises.includes(currentExercise.id)) {
            setCompletedExercises(prev => [...prev, currentExercise.id]);
            setScore(score + pointsToAdd);
        }

        setTimeout(() => {
            setFeedback(null);
            if (currentExerciseIndex < gameData.exercises.length - 1) {
                setCurrentExerciseIndex(prev => prev + 1);
            } else {
                onComplete?.(score + pointsToAdd);
            }
        }, 4000);
    };

    const getPhaseText = () => {
        switch (breathingPhase) {
            case 'inhale': return '🌬️ ĮKVĖPK LĖTAI';
            case 'hold': return '⏸️ SULAIKYK KVAPĄ';
            case 'exhale': return '💨 IŠKVĖPK ŠVELNIAI';
            case 'hold_after': return '⏸️ RAMIAI PABŪK';
            case 'ready': return '🐉 AR PASIRUOŠĘS?';
            default: return '';
        }
    };

    const getDragonScale = () => {
        if (!isActive) return 1;
        switch (breathingPhase) {
            case 'inhale': return 1 + (phaseTimer / (pattern?.inhale || 4)) * 0.4;
            case 'exhale': return 1.4 - (phaseTimer / (pattern?.exhale || 4)) * 0.4;
            case 'hold': return 1.4;
            case 'hold_after': return 1;
            default: return 1;
        }
    };

    if (!gameData || !gameData.exercises || !currentExercise) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-teal-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🐉</div>
                <h3 className="text-base md:text-3xl font-black text-teal-900 tracking-tighter uppercase">Drakonas ruošiasi...</h3>
            </div>
        );
    }

    const progress = (completedExercises.length / gameData.exercises.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Progress Area */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-teal-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
                            🧘‍♂️
                        </div>
                        <div>
                            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                                Kvėpuojantis Drakonas
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Atsipalaiduok ir mokykis kvėpuoti kartu su drakonu!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Ramybė</p>
                            <p className="text-2xl font-black text-teal-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner text-center min-w-[100px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Pratimas</p>
                            <p className="text-2xl font-black text-slate-800 leading-none">{currentExerciseIndex + 1}/{gameData.exercises.length}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 w-full bg-slate-200/50 rounded-full h-3 p-1 shadow-inner overflow-hidden border border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-teal-400 via-cyan-500 to-blue-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(20,184,166,0.3)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Main Zen Stage */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-14 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden flex flex-col items-center min-h-[50vh] justify-between">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(20,184,166,0.1)_0%,transparent_80%)] pointer-events-none" />
                <div className="absolute top-0 right-0 p-4 md:p-12 text-2xl md:text-6xl md:text-[180px] opacity-[0.03] select-none pointer-events-none text-white">✨</div>
                
                {/* Exercise Story Card */}
                <div className="bg-white/10 backdrop-blur-md rounded-xl md:rounded-[3rem] p-4 md:p-10 border-2 border-white/10 shadow-2xl max-w-2xl text-center relative z-10">
                    <h4 className="text-[10px] font-black text-teal-400 uppercase tracking-[0.5em] mb-4">Dabartinis Pratimas</h4>
                    <h3 className="text-lg md:text-4xl font-black text-white mb-6 tracking-tighter uppercase">{currentExercise.name}</h3>
                    <p className="text-teal-100/70 text-2xl font-bold italic font-serif leading-relaxed px-4">"{currentExercise.story}"</p>
                </div>

                {/* Animated Dragon Centerpiece */}
                <div className="relative py-20 flex items-center justify-center w-full">
                    <div className={`absolute w-[500px] h-[500px] rounded-full blur-[120px] transition-all duration-[2000ms] ${
                        breathingPhase === 'inhale' ? 'bg-cyan-400/20 scale-150' : 
                        breathingPhase === 'exhale' ? 'bg-teal-400/20 scale-100' : 
                        breathingPhase === 'hold' ? 'bg-amber-400/20 scale-125' : 'bg-slate-200/5'
                    }`} />

                    <div
                        className="text-[120px] md:text-[240px] drop-shadow-[0_20px_100px_rgba(20,184,166,0.4)] transition-transform duration-[1000ms] ease-in-out relative z-10"
                        style={{ transform: `scale(${getDragonScale()})` }}
                    >
                        {currentExercise.dragon_emoji}
                    </div>

                    {/* Rhythmic Breathing Rings */}
                    <div className={`absolute w-96 h-96 border-[12px] border-teal-500/20 rounded-full animate-ping ${isActive ? 'opacity-100' : 'opacity-0'}`} />
                    <div className={`absolute w-72 h-72 border-[8px] border-cyan-400/10 rounded-full animate-pulse ${isActive ? 'opacity-100' : 'opacity-0'}`} />
                </div>

                {/* Tactical Command Interface */}
                <div className="w-full max-w-2xl bg-white/5 rounded-[3.5rem] p-4 md:p-12 shadow-2xl border-2 border-white/5 text-center relative z-10 backdrop-blur-sm mb-10">
                    <div className={`text-xl md:text-5xl font-black mb-8 tracking-tighter transition-all duration-500 uppercase leading-none ${
                        breathingPhase === 'inhale' ? 'text-cyan-400' :
                        breathingPhase === 'exhale' ? 'text-teal-400' :
                        breathingPhase === 'hold' || breathingPhase === 'hold_after' ? 'text-amber-400 animate-pulse' : 'text-slate-500'
                    }`}>
                        {getPhaseText()}
                    </div>
                    
                    <div className="flex flex-col items-center justify-center gap-2">
                        {isActive ? (
                            <div className="flex items-end gap-4">
                                <div className="text-xl md:text-5xl md:text-[120px] font-black text-white leading-none tracking-tighter italic drop-shadow-[0_0_30px_rgba(255,255,255,0.2)]">
                                    {phaseTimer}
                                </div>
                                <span className="text-2xl font-black text-slate-500 uppercase tracking-widest mb-4">sek.</span>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-2xl font-black text-slate-500 uppercase tracking-[0.3em] animate-pulse italic">Pasiruošęs ramybei?</p>
                                <p className="text-sm text-slate-600 font-bold italic font-serif opacity-60">"{currentExercise.instruction}"</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Immersive Action Controls */}
                <div className="flex gap-2 md:p-6 w-full max-w-2xl relative z-10 pb-4 px-4">
                    {!isActive ? (
                        <button
                            onClick={startExercise}
                            className="premium-button-3d w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-4 md:py-10 rounded-xl md:rounded-[3rem] font-black text-lg md:text-4xl shadow-2xl shadow-teal-500/20 uppercase tracking-widest active:scale-95 transition-all"
                        >
                            PRADĖTI 🧘‍♂️
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={stopExercise}
                                className="flex-1 py-3 md:py-8 bg-slate-800 text-slate-400 rounded-lg md:rounded-[2.5rem] font-black text-2xl border-b-8 border-slate-700 hover:bg-slate-700 active:translate-y-2 active:border-b-0 transition-all uppercase tracking-widest"
                            >
                                STOP ⏸️
                            </button>
                            <button
                                onClick={completeExercise}
                                className="flex-[2] py-3 md:py-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg md:rounded-[2.5rem] font-black text-base md:text-3xl shadow-2xl shadow-emerald-500/20 uppercase tracking-widest border-b-8 border-emerald-800 active:translate-y-2 active:border-b-0 transition-all"
                            >
                                PAVYKO! ✅
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Zen Completion Overlay */}
            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className="max-w-2xl w-full p-4 md:p-16 rounded-2xl md:rounded-[4rem] bg-white shadow-[0_40px_100px_rgba(0,0,0,0.5)] relative animate-pop-in border-[12px] border-emerald-500">
                        <div className="absolute -top-4 md:p-14 left-1/2 -translate-x-1/2 w-28 h-28 bg-white rounded-full flex items-center justify-center text-2xl md:text-6xl shadow-2xl border-8 border-inherit animate-bounce">
                            🐉
                        </div>
                        
                        <div className="text-center space-y-3 md:space-y-8">
                            <h2 className="text-xl md:text-5xl font-black uppercase tracking-tighter text-emerald-600 leading-none">
                                NUOSTABI RAMYBĖ!
                            </h2>

                            <div className="bg-emerald-50 p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 border-emerald-100 text-left italic font-serif leading-relaxed">
                                <p className="text-2xl font-bold text-slate-700 text-center leading-relaxed italic">
                                    "Tu dabar esi ramus kaip drakonas savo oloje. Tavo kvėpavimas tapo tolygus ir stiprus! Puikus darbas."
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-sm font-black text-slate-300 uppercase tracking-[0.3em] ml-2">Giliname ramybę...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BreathingDragonGame;
